import { Variant } from '@kizi/core-components';
import p from './paragraphs';

const basic: Variant = {
  borderRadius: '30px',
  borderStyle: 'solid',
  borderWidth: '1px',
  cursor: 'pointer',
  fontFamily: 'button',
  fontWeight: 1,
  height: 54,
  px: 3,
  py: 1,
  textAlign: 'center',
  '&.disabled': {
    opacity: 0.3,
  },
};

const moreInformation: Variant = {
  bg: 'white',
  border: 'none',
  borderRadius: '30px',
  color: 'main.1',
  cursor: 'pointer',
  boxShadow: 'default',
  fontSize: 2,
  fontWeight: 1,
  lineHeight: 2,
  px: 3,
  py: 1,
  '&:active:enabled': {
    '&:not(.disabled)': {
      bg: 'white',
      border: 'none',
      color: 'main.2',
    },
  },
  '&:hover:enabled': {
    '&:not(.disabled)': {
      bg: 'white',
      border: 'none',
      color: 'main.1',
    },
  },
  '&.disabled': {
    color: 'second.0',
  },
};

const primary: Variant = {
  ...basic,
  bg: 'main.1',
  borderColor: 'main.1',
  color: 'white',
  fontSize: 3,
  height: 54,
  px: 6,
  py: 3,
  width: ['100%', 'fit-content'],
  '&:disabled': {
    opacity: 0.3,
  },
  '&:active:enabled': {
    '&:not(.disabled)': {
      bg: 'main.2',
      borderColor: 'main.2',
    },
  },
  '&:hover:enabled': {
    '&:not(.disabled)': {
      bg: 'main.1',
      borderColor: 'main.1',
    },
  },
};

const primarySmall: Variant = {
  ...primary,
  fontSize: 2,
  height: '',
  px: 3,
  py: 1,
  width: 'max-content',
  float: 'right',
};

const secondary: Variant = {
  ...basic,
  bg: 'white',
  borderColor: 'main.1',
  color: 'main.1',
  fontSize: 3,
  px: 5,
  py: 3,
  '&:active:enabled': {
    '&:not(.disabled)': {
      borderColor: 'main.2',
      color: 'main.2',
    },
  },
  '&:hover:enabled': {
    '&:not(.disabled)': {
      borderColor: 'main.1',
    },
  },
};

const secondarySmall: Variant = {
  ...secondary,
  fontSize: [0, null, 2],
  height: '',
  px: 3,
  py: 1,
  width: 'fit-content',
};

const secondaryMedium: Variant = {
  ...secondary,
  fontSize: [0, null, 2],
  height: '31px',
  px: 3,
  py: 1,
  pt: 0,
  width: 'fit-content',
  marginTop: 3,
  display: 'block',
};

const secondaryTransparent: Variant = {
  ...secondary,
  bg: 'transparent',
};

const transparent: Variant = {
  border: 'none',
  bg: 'transparent',
  color: 'main.1',
  cursor: 'pointer',
  fontSize: [2, null, 3],
  lineHeight: 2,
  p: 0,
  '&:active:enabled, &:hover:enabled': {
    '&:not(.disabled)': {
      border: 'none',
      bg: 'transparent',
      color: 'main.2',
      textDecorationLine: 'underline',
    },
  },
};

const transparentMedium: Variant = {
  border: 'none',
  bg: 'transparent',
  cursor: 'pointer',
  fontSize: [2, null, 3],
  lineHeight: [3, 2, 2],
  p: 0,
  mt: 'auto',
  ml: ['auto', 3, 3],
  height: ['5px', '83px'],
  color: 'supplementary.0',
};

const transparentSmall: Variant = {
  ...p.footnoteRegular,
  border: 'none',
  bg: 'transparent',
  color: 'main.1',
  cursor: 'pointer',
  lineHeight: 2,
  p: 0,
  '&:active:enabled, &:hover:enabled': {
    '&:not(.disabled)': {
      border: 'none',
      bg: 'transparent',
      color: 'main.2',
      textDecorationLine: 'underline',
    },
  },
};

const transparentSmallImportant: Variant = {
  ...p.footnoteImportant,
  border: 'none',
  bg: 'transparent',
  color: 'main.1',
  cursor: 'pointer',
  lineHeight: 2,
  p: 0,
  '&:active:enabled, &:hover:enabled': {
    '&:not(.disabled)': {
      border: 'none',
      bg: 'transparent',
      color: 'main.2',
      textDecorationLine: 'underline',
    },
  },
};

const transparentToExtraSmall: Variant = {
  ...p.bodyRegularToCaption,
  border: 'none',
  bg: 'transparent',
  color: 'main.1',
  cursor: 'pointer',
  lineHeight: 2,
  p: 0,
  '&:active:enabled, &:hover:enabled': {
    '&:not(.disabled)': {
      border: 'none',
      bg: 'transparent',
      color: 'main.2',
      textDecorationLine: 'underline',
    },
  },
};

const transparentExtraSmall: Variant = {
  ...p.captionRegularSmall,
  border: 'none',
  bg: 'transparent',
  color: 'main.1',
  cursor: 'pointer',
  m: 0,
  p: 0,
  '&:active:enabled, &:hover:enabled': {
    '&:not(.disabled)': {
      border: 'none',
      bg: 'transparent',
      color: 'main.2',
      textDecorationLine: 'underline',
    },
  },
};

const transparentExtraSmallImportant: Variant = {
  ...p.captionImportantSmall,
  border: 'none',
  borderRadius: 'initial',
  bg: 'transparent',
  color: 'main.1',
  cursor: 'pointer',
  lineHeight: '0.875rem',
  m: 0,
  p: 0,
  width: 'fit-content',
  '&:active:enabled, &:hover:enabled': {
    '&:not(.disabled)': {
      border: 'none',
      bg: 'transparent',
      color: 'main.2',
      textDecorationLine: 'underline',
    },
  },
};

const transparentWithIcon: Variant = {
  ...transparent,
  '> svg': {
    fill: 'main.1',
  },
};

const applyPageNavigation: Variant = {
  ...transparent,
  color: 'main.2',
  display: 'inline-block',
  '&:not(:last-child)': {
    '::after': {
      content: '""',
      height: '1px',
      width: '40px',
      bg: 'supplementary.6',
      display: 'inline-block',
      verticalAlign: 'middle',
      mr: 3,
      ml: 3,
    },
  },
  '&:disabled': {
    color: 'supplementary.6',
  },
};

const transparentDefaultColor: Variant = {
  ...transparent,
  color: 'main.3',
};

const promotionItem: Variant = {
  ...transparent,
  fontSize: 1,
  color: 'main.2',
  '> span': {
    pb: '3px',
    borderBottom: '1px dashed',
    borderBottomColor: 'lightMain1',
  },
  '> svg': {
    width: '16px',
    height: '16px',
    minWidth: '16px',
    alignSelf: 'baseline',
  },
};

const promotionItemLarge: Variant = {
  ...promotionItem,
  color: 'text',
  fontSize: 2,
  fontWeight: 1,
};

const info: Variant = {
  bg: 'transparent',
  border: 'none',
  color: 'main.1',
  cursor: 'pointer',
  fontWeight: 1,
  mt: 6,
  p: 0,
  '&:active:enabled, &:hover:enabled': {
    '&:not(.disabled)': {
      border: 'none',
      bg: 'transparent',
      color: 'main.1',
    },
  },
};

const primaryHeading: Variant = {
  ...primary,
  fontSize: 3,
  fontFamily: 'heading',
};

const submitModalWithSteps: Variant = {
  ...primary,
  width: 'fit-content',
};

const addMore = {
  border: '1px dashed',
  borderColor: 'second.1',
  borderRadius: '8px',
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 3,
  background: 'transparent',
  cursor: 'pointer',
};

const expandProductCard: Variant = {
  bg: 'white',
  border: 'none',
  borderRadius: '200px',
  boxShadow: 'default',
  padding: '4px 16px',
  fontSize: '14px',
  lineHeight: '21px',
  fontWeight: 700,
  color: 'main.1',
  cursor: 'pointer',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  bottom: '-14px',
};

const productCardPrimary: Variant = {
  ...basic,
  backgroundColor: 'main.1',
  color: 'white',
  lineHeight: '24px',
  fontSize: '16px',
  padding: '8px 16px',
  boxShadow: '0px 4px 12px 0px #3E486524, 0px 0px 2px 0px #4E5A7E14',
  borderRadius: '30px',
  width: '100%',
  height: 'auto',
  border: 'unset',

  '&:disabled': {
    opacity: 0.3,
    boxShadow: 'none',
  },
  '&:focus:enabled': {
    '&:not(.disabled)': {
      opacity: 0.8,
    },
  },
  '&:hover:enabled': {
    '&:not(.disabled)': {
      opacity: 0.8,
    },
  },
};

const buttonVariants = {
  productCardPrimary,
  info,
  moreInformation,
  primary,
  primarySmall,
  secondary,
  secondaryTransparent,
  secondarySmall,
  secondaryMedium,
  submitModalWithSteps,
  transparent,
  transparentMedium,
  transparentSmall,
  transparentSmallImportant,
  transparentToExtraSmall,
  transparentExtraSmall,
  transparentExtraSmallImportant,
  transparentWithIcon,
  promotionItem,
  promotionItemLarge,
  primaryHeading,
  addMore,
  transparentDefaultColor,
  applyPageNavigation,
  expandProductCard,
};

export default buttonVariants;
